// Local Workout Store - Persists in-progress workout data
// Enables resuming workouts after browser close/refresh and offline support
// Uses cross-platform storage (native on Android/iOS, localStorage on web)

import * as storage from "@/lib/storage";
import { Capacitor } from "@capacitor/core";
import type { ProgramExerciseVariantConfig } from "@/lib/programs/variants";
import type { ActivityLogDraft } from "@/lib/training-types/types";
import {
  getScopedStorageKey,
  getScopedStorageKeyCandidates,
} from "@/lib/sync/storage-scope";

const WORKOUT_STORE_KEY = "inProgressWorkout";

export type StoredSetData = {
  set_number: number;
  reps: number | null;
  weight: number | null;
  weight_unit: string;
  rpe: number | null;
  is_warmup: boolean;
  set_type?: string;
};

export type StoredExerciseData = {
  id: string;
  order_index?: number;
  training_type?: string | null;
  sets: number;
  warmup_sets?: number | null;
  reps: string;
  weight_prescription: string | null;
  rest_seconds: number | null;
  target_duration_seconds?: number | null;
  target_distance_meters?: number | null;
  cardio_params?: Record<string, unknown> | null;
  target_rounds?: number | null;
  work_seconds?: number | null;
  rest_between_rounds_seconds?: number | null;
  reps_per_round?: string | null;
  interval_params?: Record<string, unknown> | null;
  notes: string | null;
  default_setup_signature?: string | null;
  default_setup_json?: Partial<Record<"pin" | "width" | "grip" | "axis" | "foot_position", string>> | null;
  variant_signature?: string | null;
  variant_config?: ProgramExerciseVariantConfig | null;
  exercises: {
    id: string;
    name: string;
    muscle_groups: string[];
    equipment?: string | null;
    demo_video_url?: string | null;
    external_link?: string | null;
    default_training_type?: string | null;
  } | null;
};

export type StoredModification = {
  type: "added-set" | "removed-set" | "swapped-exercise" | "added-exercise" | "removed-exercise";
  exerciseName: string;
  originalExerciseName?: string;
  details?: string;
};

export type StoredWorkoutData = {
  sessionId: string;
  programDayId: string | null;
  programDayName: string;
  activeExerciseIndex: number;
  sets: Record<string, StoredSetData[]>; // programExerciseId -> sets
  savedSetNumbers: Record<string, number[]>; // programExerciseId -> completed set numbers
  sessionNotes: string;
  lastUpdated: string;
  // New fields for exercise persistence
  exercises?: StoredExerciseData[];
  modifications?: StoredModification[];
  originalExerciseIds?: string[]; // Track which exercises were in the original routine
  // PR detection state
  prSets?: Record<string, string>; // "programExerciseId:setNumber" -> PR description
  sessionBestWeights?: Record<string, number>; // exerciseId -> best weight in kg this session
  activityDrafts?: Record<string, ActivityLogDraft>;
  setupSelections?: Record<string, Partial<Record<"pin" | "width" | "grip" | "axis" | "foot_position", string>>>;
  exerciseCustomLabels?: Record<string, string[]>;
  exerciseVariationNotes?: Record<string, string>;
};

const isNative = typeof window !== "undefined" && Capacitor.isNativePlatform();

function parseStoredWorkoutSnapshot(raw: string | null): StoredWorkoutData | null {
  if (!raw) return null;

  try {
    return JSON.parse(raw) as StoredWorkoutData;
  } catch {
    return null;
  }
}

function getStoredWorkoutRank(data: StoredWorkoutData): number {
  const updatedAtMs = new Date(data.lastUpdated).getTime();
  return Number.isFinite(updatedAtMs) ? updatedAtMs : -Infinity;
}

function pickPreferredStoredWorkout(
  snapshots: Array<{ key: string; data: StoredWorkoutData }>
): { key: string; data: StoredWorkoutData } | null {
  let best: { key: string; data: StoredWorkoutData } | null = null;
  let bestRank = -Infinity;

  for (const snapshot of snapshots) {
    const rank = getStoredWorkoutRank(snapshot.data);
    if (rank > bestRank) {
      best = snapshot;
      bestRank = rank;
    }
  }

  return best;
}

async function loadPreferredStoredWorkoutAsync(): Promise<{ key: string; data: StoredWorkoutData } | null> {
  const snapshots: Array<{ key: string; data: StoredWorkoutData }> = [];
  for (const key of getScopedStorageKeyCandidates(WORKOUT_STORE_KEY)) {
    const parsed = parseStoredWorkoutSnapshot(await storage.getItem(key));
    if (parsed) {
      snapshots.push({ key, data: parsed });
    }
  }
  return pickPreferredStoredWorkout(snapshots);
}

function loadPreferredStoredWorkoutSync(): { key: string; data: StoredWorkoutData } | null {
  const snapshots: Array<{ key: string; data: StoredWorkoutData }> = [];
  for (const key of getScopedStorageKeyCandidates(WORKOUT_STORE_KEY)) {
    const parsed = parseStoredWorkoutSnapshot(localStorage.getItem(key));
    if (parsed) {
      snapshots.push({ key, data: parsed });
    }
  }
  return pickPreferredStoredWorkout(snapshots);
}

async function cleanupScopedWorkoutStoreFallbacksAsync(): Promise<void> {
  const [primaryKey, ...fallbackKeys] = getScopedStorageKeyCandidates(WORKOUT_STORE_KEY);
  await Promise.all(
    fallbackKeys
      .filter((key) => key !== primaryKey)
      .map((key) => storage.removeItem(key))
  );
}

function cleanupScopedWorkoutStoreFallbacksSync(): void {
  const [primaryKey, ...fallbackKeys] = getScopedStorageKeyCandidates(WORKOUT_STORE_KEY);
  for (const key of fallbackKeys) {
    if (key === primaryKey) continue;
    localStorage.removeItem(key);
  }
}

// Helper to check if data is stale (more than 24 hours old)
function isStale(lastUpdated: string): boolean {
  const lastUpdatedDate = new Date(lastUpdated);
  const now = new Date();
  const hoursSinceUpdate = (now.getTime() - lastUpdatedDate.getTime()) / (1000 * 60 * 60);
  return hoursSinceUpdate > 24;
}

/**
 * Check if a stored workout session is likely abandoned.
 * A session is considered abandoned if it hasn't been updated in over 4 hours,
 * which is less aggressive than the 24-hour isStale check.
 * The CTA logic can use this to avoid showing "Continue Workout" for
 * sessions the user has clearly left behind.
 */
export function isStoredWorkoutLikelyAbandoned(data: StoredWorkoutData): boolean {
  const lastUpdatedMs = new Date(data.lastUpdated).getTime();
  if (!Number.isFinite(lastUpdatedMs)) return true;
  const hoursSinceUpdate = (Date.now() - lastUpdatedMs) / (1000 * 60 * 60);
  return hoursSinceUpdate > 4;
}

function remapRecordKey<T>(record: Record<string, T> | undefined, previousKey: string, nextKey: string): Record<string, T> | undefined {
  if (!record || !(previousKey in record)) return record;
  const nextRecord = { ...record };
  if (!(nextKey in nextRecord)) {
    nextRecord[nextKey] = nextRecord[previousKey] as T;
  }
  delete nextRecord[previousKey];
  return nextRecord;
}

function removeRecordKey<T>(record: Record<string, T> | undefined, keyToRemove: string): Record<string, T> | undefined {
  if (!record || !(keyToRemove in record)) return record;
  const nextRecord = { ...record };
  delete nextRecord[keyToRemove];
  return nextRecord;
}

function remapPrefixedRecordKey<T>(record: Record<string, T> | undefined, previousKey: string, nextKey: string): Record<string, T> | undefined {
  if (!record) return record;
  const prefix = `${previousKey}:`;
  const entries = Object.entries(record).filter(([key]) => key.startsWith(prefix));
  if (entries.length === 0) return record;
  const nextRecord = { ...record };
  for (const [key, value] of entries) {
    delete nextRecord[key];
    const suffix = key.slice(prefix.length);
    const nextCompositeKey = `${nextKey}:${suffix}`;
    if (!(nextCompositeKey in nextRecord)) {
      nextRecord[nextCompositeKey] = value;
    }
  }
  return nextRecord;
}

function removePrefixedRecordKey<T>(record: Record<string, T> | undefined, keyToRemove: string): Record<string, T> | undefined {
  if (!record) return record;
  const prefix = `${keyToRemove}:`;
  const entries = Object.keys(record).filter((key) => key.startsWith(prefix));
  if (entries.length === 0) return record;
  const nextRecord = { ...record };
  for (const key of entries) {
    delete nextRecord[key];
  }
  return nextRecord;
}

export function reconcileStoredWorkoutSessionExerciseSuccess(
  data: StoredWorkoutData,
  params: {
    sessionId: string;
    tempProgramExerciseId: string;
    tempExerciseId: string;
    nextProgramExerciseId: string;
    nextExercise: StoredExerciseData["exercises"];
  }
): StoredWorkoutData {
  const {
    sessionId,
    tempProgramExerciseId,
    tempExerciseId,
    nextProgramExerciseId,
    nextExercise,
  } = params;
  if (data.sessionId !== sessionId) return data;

  const nextExercises = data.exercises?.map((exercise) =>
    exercise.id === tempProgramExerciseId
      ? {
          ...exercise,
          id: nextProgramExerciseId,
          training_type:
            nextExercise?.default_training_type ??
            exercise.training_type ??
            null,
          exercises: nextExercise,
        }
      : exercise
  );

  return {
    ...data,
    exercises: nextExercises,
    sets: remapRecordKey(data.sets, tempProgramExerciseId, nextProgramExerciseId) ?? data.sets,
    savedSetNumbers:
      remapRecordKey(data.savedSetNumbers, tempProgramExerciseId, nextProgramExerciseId) ??
      data.savedSetNumbers,
    prSets:
      remapPrefixedRecordKey(data.prSets, tempProgramExerciseId, nextProgramExerciseId) ??
      data.prSets,
    activityDrafts:
      remapRecordKey(data.activityDrafts, tempProgramExerciseId, nextProgramExerciseId) ??
      data.activityDrafts,
    setupSelections:
      remapRecordKey(data.setupSelections, tempProgramExerciseId, nextProgramExerciseId) ??
      data.setupSelections,
    exerciseCustomLabels:
      remapRecordKey(data.exerciseCustomLabels, tempProgramExerciseId, nextProgramExerciseId) ??
      data.exerciseCustomLabels,
    exerciseVariationNotes:
      remapRecordKey(data.exerciseVariationNotes, tempProgramExerciseId, nextProgramExerciseId) ??
      data.exerciseVariationNotes,
    sessionBestWeights:
      remapRecordKey(data.sessionBestWeights, tempExerciseId, nextExercise?.id || tempExerciseId) ??
      data.sessionBestWeights,
  };
}

export function reconcileStoredWorkoutSessionExerciseDropped(
  data: StoredWorkoutData,
  params: {
    sessionId: string;
    tempProgramExerciseId: string;
    tempExerciseId: string;
    exerciseName: string;
  }
): StoredWorkoutData {
  const { sessionId, tempProgramExerciseId, tempExerciseId, exerciseName } = params;
  if (data.sessionId !== sessionId) return data;

  const removedIndex = data.exercises?.findIndex((exercise) => exercise.id === tempProgramExerciseId) ?? -1;
  const nextExercises =
    removedIndex >= 0 ? data.exercises?.filter((exercise) => exercise.id !== tempProgramExerciseId) : data.exercises;
  const nextActiveExerciseIndex =
    removedIndex === -1
      ? data.activeExerciseIndex
      : removedIndex >= (nextExercises?.length ?? 0)
        ? Math.max(0, (nextExercises?.length ?? 1) - 1)
        : data.activeExerciseIndex > removedIndex
          ? data.activeExerciseIndex - 1
          : data.activeExerciseIndex === removedIndex
            ? Math.max(0, removedIndex - 1)
            : data.activeExerciseIndex;

  return {
    ...data,
    activeExerciseIndex: nextActiveExerciseIndex,
    exercises: nextExercises,
    sets: removeRecordKey(data.sets, tempProgramExerciseId) ?? data.sets,
    savedSetNumbers:
      removeRecordKey(data.savedSetNumbers, tempProgramExerciseId) ?? data.savedSetNumbers,
    prSets:
      removePrefixedRecordKey(data.prSets, tempProgramExerciseId) ?? data.prSets,
    activityDrafts:
      removeRecordKey(data.activityDrafts, tempProgramExerciseId) ?? data.activityDrafts,
    setupSelections:
      removeRecordKey(data.setupSelections, tempProgramExerciseId) ?? data.setupSelections,
    exerciseCustomLabels:
      removeRecordKey(data.exerciseCustomLabels, tempProgramExerciseId) ?? data.exerciseCustomLabels,
    exerciseVariationNotes:
      removeRecordKey(data.exerciseVariationNotes, tempProgramExerciseId) ?? data.exerciseVariationNotes,
    sessionBestWeights:
      removeRecordKey(data.sessionBestWeights, tempExerciseId) ?? data.sessionBestWeights,
    modifications: data.modifications?.filter(
      (modification) =>
        !(modification.type === "added-exercise" && modification.exerciseName === exerciseName)
    ),
  };
}

async function updateStoredWorkoutAsync(
  sessionId: string,
  updater: (data: StoredWorkoutData) => StoredWorkoutData
): Promise<void> {
  const stored = await loadWorkoutFromStoreAsync();
  if (!stored || stored.sessionId !== sessionId) return;
  await saveWorkoutToStoreAsync(updater(stored));
}

// ============================================
// ASYNC FUNCTIONS (cross-platform, preferred)
// ============================================

/**
 * Save workout state to storage (async, cross-platform)
 */
export async function saveWorkoutToStoreAsync(data: StoredWorkoutData): Promise<void> {
  if (typeof window === "undefined") return;
  
  try {
    await storage.setJSON(getScopedStorageKey(WORKOUT_STORE_KEY), {
      ...data,
      lastUpdated: new Date().toISOString(),
    });
    await cleanupScopedWorkoutStoreFallbacksAsync();
  } catch (error) {
    console.error("Failed to save workout to store:", error);
  }
}

/**
 * Load workout state from storage (async, cross-platform)
 */
export async function loadWorkoutFromStoreAsync(): Promise<StoredWorkoutData | null> {
  if (typeof window === "undefined") return null;
  
  try {
    const snapshot = await loadPreferredStoredWorkoutAsync();
    if (!snapshot) return null;
    const data = snapshot.data;
    
    // Check if data is stale (more than 24 hours old)
    if (isStale(data.lastUpdated)) {
      await clearWorkoutStoreAsync();
      return null;
    }

    const storageKey = getScopedStorageKey(WORKOUT_STORE_KEY);
    if (snapshot.key !== storageKey) {
      await storage.setJSON(storageKey, data);
    }
    await cleanupScopedWorkoutStoreFallbacksAsync();
    
    return data;
  } catch (error) {
    console.error("Failed to load workout from store:", error);
    return null;
  }
}

/**
 * Clear workout from storage (async, cross-platform)
 */
export async function clearWorkoutStoreAsync(): Promise<void> {
  if (typeof window === "undefined") return;
  
  try {
    await Promise.all([
      ...getScopedStorageKeyCandidates(WORKOUT_STORE_KEY).map((key) => storage.removeItem(key)),
      storage.removeItem(WORKOUT_STORE_KEY),
    ]);
  } catch (error) {
    console.error("Failed to clear workout store:", error);
  }
}

/**
 * Check if there's a stored workout (async, cross-platform)
 */
export async function hasStoredWorkoutAsync(sessionId?: string): Promise<boolean> {
  const stored = await loadWorkoutFromStoreAsync();
  if (!stored) return false;
  if (sessionId) return stored.sessionId === sessionId;
  return true;
}

/**
 * Get the session ID of any stored workout (async, cross-platform)
 */
export async function getStoredWorkoutSessionIdAsync(): Promise<string | null> {
  const stored = await loadWorkoutFromStoreAsync();
  return stored?.sessionId ?? null;
}

export async function reconcileStoredWorkoutSessionIdAsync(
  oldSessionId: string,
  newSessionId: string
): Promise<void> {
  const stored = await loadWorkoutFromStoreAsync();
  if (!stored || stored.sessionId !== oldSessionId) return;
  await saveWorkoutToStoreAsync({ ...stored, sessionId: newSessionId });
}

export async function reconcileStoredWorkoutSessionExerciseSuccessAsync(params: {
  sessionId: string;
  tempProgramExerciseId: string;
  tempExerciseId: string;
  nextProgramExerciseId: string;
  nextExercise: StoredExerciseData["exercises"];
}): Promise<void> {
  await updateStoredWorkoutAsync(params.sessionId, (data) =>
    reconcileStoredWorkoutSessionExerciseSuccess(data, params)
  );
}

export async function reconcileStoredWorkoutSessionExerciseDroppedAsync(params: {
  sessionId: string;
  tempProgramExerciseId: string;
  tempExerciseId: string;
  exerciseName: string;
}): Promise<void> {
  await updateStoredWorkoutAsync(params.sessionId, (data) =>
    reconcileStoredWorkoutSessionExerciseDropped(data, params)
  );
}

// ============================================
// SYNC FUNCTIONS (web only, for backwards compatibility)
// On native platforms, these return null/void and log warnings
// ============================================

/**
 * Save workout state to localStorage (sync, web only)
 * @deprecated Use saveWorkoutToStoreAsync for cross-platform support
 */
export function saveWorkoutToStore(data: StoredWorkoutData): void {
  if (typeof window === "undefined") return;
  
  if (isNative) {
    // On native, we can't write synchronously - fire and forget
    saveWorkoutToStoreAsync(data);
    return;
  }
  
  try {
    localStorage.setItem(getScopedStorageKey(WORKOUT_STORE_KEY), JSON.stringify({
      ...data,
      lastUpdated: new Date().toISOString(),
    }));
    cleanupScopedWorkoutStoreFallbacksSync();
  } catch (error) {
    console.error("Failed to save workout to store:", error);
  }
}

/**
 * Load workout state from localStorage (sync, web only)
 * @deprecated Use loadWorkoutFromStoreAsync for cross-platform support
 */
export function loadWorkoutFromStore(): StoredWorkoutData | null {
  if (typeof window === "undefined") return null;
  
  if (isNative) {
    // On native, we can't read synchronously - return null
    // Callers should use the async version
    console.warn("loadWorkoutFromStore called on native - use loadWorkoutFromStoreAsync");
    return null;
  }
  
  try {
    const snapshot = loadPreferredStoredWorkoutSync();
    if (!snapshot) return null;
    const data = snapshot.data;
    
    // Check if data is stale (more than 24 hours old)
    if (isStale(data.lastUpdated)) {
      clearWorkoutStore();
      return null;
    }

    const storageKey = getScopedStorageKey(WORKOUT_STORE_KEY);
    if (snapshot.key !== storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(data));
    }
    cleanupScopedWorkoutStoreFallbacksSync();
    
    return data;
  } catch (error) {
    console.error("Failed to load workout from store:", error);
    return null;
  }
}

/**
 * Clear workout from localStorage (sync, web only)
 * @deprecated Use clearWorkoutStoreAsync for cross-platform support
 */
export function clearWorkoutStore(): void {
  if (typeof window === "undefined") return;
  
  if (isNative) {
    // On native, fire and forget
    clearWorkoutStoreAsync();
    return;
  }
  
  try {
    for (const key of getScopedStorageKeyCandidates(WORKOUT_STORE_KEY)) {
      localStorage.removeItem(key);
    }
    localStorage.removeItem(WORKOUT_STORE_KEY);
  } catch (error) {
    console.error("Failed to clear workout store:", error);
  }
}

/**
 * Check if there's a stored workout for a specific session (sync, web only)
 * @deprecated Use hasStoredWorkoutAsync for cross-platform support
 */
export function hasStoredWorkout(sessionId?: string): boolean {
  if (isNative) {
    // Can't check synchronously on native
    return false;
  }
  
  const stored = loadWorkoutFromStore();
  if (!stored) return false;
  if (sessionId) return stored.sessionId === sessionId;
  return true;
}

/**
 * Get the session ID of any stored workout (sync, web only)
 * @deprecated Use getStoredWorkoutSessionIdAsync for cross-platform support
 */
export function getStoredWorkoutSessionId(): string | null {
  if (isNative) {
    // Can't read synchronously on native
    return null;
  }
  
  const stored = loadWorkoutFromStore();
  return stored?.sessionId ?? null;
}
